import { put, race, take, call, select, delay } from 'redux-saga/effects'
import axios from '../../axios-local-api';
import * as actions from '../actions';
import { STOP_RSC_POLLING } from '../actions/actionTypes';

const extractFirstIndex = res => {
  return new Date(res.data.speedTestResults[0].timestamp).getTime()
}

const parseSpeedCheckResults = res => {
  const { speedTestResults = [] } = res.data;
  
  return speedTestResults.map(result => ({
    timestamp: new Date(result.timestamp).getTime(),
    up: result.uploadSpeed,
    down: result.downloadSpeed,
    serverName: result.serverName || 'N/A',
    serverId: result.serverId || 'unknown',
    testType: result.testType || 'N/A',
    sourceSystem: result.sourceSystem || 'N/A',
    subscribedUpSpeed: result.subscribedUpload || null,
    subscribedDownSpeed: result.subscribedDownload || null,
    pass: result.pass ?? 'N/A'
  }));
}

const startTimeFromReduxStore = (state) => state.speedCheck.speedCheckStartDate

const hasSpeedCheckEndedSuccessfully = (speedCheckFirstIndex, speedCheckStartTime) => speedCheckFirstIndex > speedCheckStartTime

const checkForTimeOut = (startTime, timeoutValue) => {
  const t = timeoutValue === 'true' ? 1000 : 90000
  if (new Date().getTime() - startTime >= t) {
    throw new Error('TIME OUT ON POLLING')
  }
}

export function* startRouterSpeedCheckSaga() {
  const routerMac = yield sessionStorage.getItem('mac');
  const sessionToken = yield sessionStorage.getItem('sessionToken');
  const techMobile = yield sessionStorage.getItem('techMobile');
  const browser = yield techMobile === 'true' ? "TECH_MOBILE" : "WEB_CLIENT"
  const timeoutValue = yield sessionStorage.getItem('enableSpeedCheckTimeoutTestingValue')
  const t = timeoutValue === 'true' ? 0 : 30000
  if (sessionToken) {
    try {
      // 1. Submit `Start Speed Check` Request
      let { data } = yield axios.post('/speedTest/start', null, { params: { routerMac } });

      // 2. Store the response
      yield put(actions.setSpeedCheckStartDate(new Date(data.createdAt).getTime()));

      // 3.0 Wait 20s (average response time is ~25s)
      yield delay(t)

      // 3.1 Start Polling
      yield call(resultsPollingWatcher)

    } catch (startSpeedCheckError) {
      yield put(actions.logSpeedCheckResults(routerMac, browser, new Date().getTime(), `WITH ERROR: ${startSpeedCheckError}`))
      yield put(actions.setRouterSpeedResultsFail(startSpeedCheckError));
    }
  } else {
    console.error('NO TOKEN FOUND')
  }
}

export function* fetchRouterSpeedCheckResultsSaga() {
  const routerMac = yield sessionStorage.getItem('mac');
  const sessionToken = yield sessionStorage.getItem('sessionToken');

  if (sessionToken) {
    try {
      let rscResultsRes = yield axios.get('/speedTest/results', { params: { routerMac } })
      yield put(actions.setResultsLoaded(false));
      yield put(actions.setResultsLoading(true));
      let parsedRouterSpeedCheckResults = yield parseSpeedCheckResults(rscResultsRes)
      yield put(actions.setSpeedCheckResults(parsedRouterSpeedCheckResults));
      yield put(actions.setResultsLoaded(true));
      yield put(actions.setResultsLoading(false));
    } catch (speedCheckResultsError) {
      yield put(actions.setRouterSpeedResultsFail(speedCheckResultsError))
      // yield put(actions.setResultsLoading(false));
    }
  } else {
    yield put(actions.setResultsLoaded(true));
    yield put(actions.setResultsLoading(false));
    //console.log('NO TOKEN')
  }
}

// export function* checkRouterSpeedCheckVisibilitySaga() {
//   const routerMac = yield sessionStorage.getItem('mac');
//   const sessionToken = yield sessionStorage.getItem('sessionToken');
//   if (sessionToken) {
//     try {
//       let response = yield axios.put('speedTest/enable',
//         {
//           'enable': true,
//           'enableAllNodes': true
//         },
//         {
//           params: { routerMac }
//         });
//       yield put(actions.setRouterSpeedVisiblity(response && response.status === 200))
//     } catch (speedCheckNotAvailableError) {
//       yield put(actions.setRouterSpeedResultsFail(speedCheckNotAvailableError));
//     }
//   } else {
//     console.log('NO TOKEN')
//   }
// }

export function* resultsPollingWatcher() {
  yield race({
    task: call(resultsPollingWorker),
    cancel: take(STOP_RSC_POLLING)
  })
}

const POLL_DELAY = 5000

export function* resultsPollingWorker() {
  while (1) {
    try {
      // Fetch the start time from Store and routerMac
      const speedTestStartedAt = yield select(startTimeFromReduxStore)
      const routerMac = yield sessionStorage.getItem('mac');
      const timeoutValue = yield sessionStorage.getItem('enableSpeedCheckTimeoutTestingValue')
      const techMobile = yield sessionStorage.getItem('techMobile');
      const browser = yield techMobile === 'true' ? "TECH_MOBILE" : "WEB_CLIENT"

      // Check for timeout
      try {
        yield checkForTimeOut(speedTestStartedAt, timeoutValue)
      } catch (timeOutError) {
        yield put(actions.logSpeedCheckResults(routerMac, browser, (new Date().getTime() - speedTestStartedAt), `FAILED`))
        yield put(actions.setRouterSpeedResultsFail(timeOutError))
        yield put({ type: STOP_RSC_POLLING })
      }

      // Fetch results
      try {
        const rscResultsRes = yield axios.get('/speedTest/results', { params: { routerMac } })
        const parsedRouterSpeedCheckResults = yield parseSpeedCheckResults(rscResultsRes)
        const firstIndex = yield extractFirstIndex(rscResultsRes)

        yield put(actions.setSpeedCheckResults(parsedRouterSpeedCheckResults))

        const speedTestCompletedSuccessfully = yield hasSpeedCheckEndedSuccessfully(firstIndex, speedTestStartedAt)

        if (speedTestCompletedSuccessfully === true) {
          yield put(actions.logSpeedCheckResults(routerMac, browser, (new Date().getTime() - speedTestStartedAt), `SUCCESS`))
          yield put(actions.setSpeedCheckStartDate(null))
          yield put({ type: STOP_RSC_POLLING })
        } else {
          yield delay(POLL_DELAY)
        }
      } catch (errorWhilePolling) {
        if (errorWhilePolling.response && errorWhilePolling.response.status === 404) {
          yield delay(POLL_DELAY)
        } else {
          yield put(actions.logSpeedCheckResults(routerMac, browser, (new Date().getTime() - speedTestStartedAt), `WITH ERROR: ${errorWhilePolling}`))
          yield put(actions.setRouterSpeedResultsFail(errorWhilePolling))
          yield put({ type: STOP_RSC_POLLING })
        }
      }
    } catch (fetchingLocalStateError) {
      yield put(actions.logSpeedCheckResults(routerMac, browser, undefined, `WITH ERROR: ${fetchingLocalStateError}`))
      yield put(actions.setRouterSpeedResultsFail(fetchingLocalStateError))
      yield put({ type: STOP_RSC_POLLING })
    }
  }
}

export function* logSpeedCheckSaga(action) {
  // REQUIREMENTS FROM WIN-309
  // status -> "SUCCESSFUL", "FAILED", "WITH ERROR"
  // "SUCCESSFUL": every thing went fine
  // "FAILED": NO RESPOSNE containing RESULTS (e.g. timeout)
  // "WITH ERROR": this case occurs when the initial call to START speed test fails (INCLUDE ERROR DATA)

  const { routerMac, browser, elapsedTime, status } = yield action

  const rscLoggingEndpoint = yield {
    method: 'post',
    url: '/speedTest/logSpeedTestEvent',
    data: {
      routerMac,
      browser,
      duration: elapsedTime,
      status,
    }
  }

  try {
    yield call(axios, rscLoggingEndpoint);
  } catch (e) {
    yield console.error('Error while attempting to log Speed Check event', e);
  }
}